import React, { useState, useEffect, useRef, Suspense, useMemo } from "react";
import { PropTypes } from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import _ from "lodash";

//components
import Spinner from "../../../../common/Spinner";
import AppHOC from "../../../../generics/AppHOC";
const ErrorComponent = React.lazy(() =>
  import("../../../../common/ErrorComponent")
);
const NoDataAvailable = React.lazy(() =>
  import("../../../../common/NoDataAvailable")
);

//utils
import { getTableColumns } from "./sitesTableConfig";
import useApplicationInfoHooks from "../../useApplicationInfoHooks";
import {
  getLocalStorageFlag,
  setLocalStorageFlag,
  downloadAsCSVFile,
  escapeCSVField,
} from "../../../../utils/common";
import { validateProps } from "../../../../utils/waniCommon";
import useI18n from "../../../../i18n/useI18n";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import TableStore from "@harbor/elements/utils/table/store";
import { LOCAL_STORAGE_LABELS } from "../../../../utils/enums";
import SettingsDrawer from "../../../../common/SettingsDrawer";
import { useTableSettings } from "../../../../utils/table";
import OverLappingSpinner from "../../../../common/Spinner/OverLappingSpinner";
import NoData from "../../../../common/table/NoData";

//style
import css from "../../insightsAppMagneticStyle.less";

const mapStateToProps = (state) => {
  return {
    predictiveNetworks: state.vanalytics.predictiveNetworks,
  };
};
const [
  HbrCard,
  HbrInput,
  HbrIcon,
  HbrTable,
  HbrButton,
  HbrSpinner,
  HbrPagination,
] = reactWrapper([
  "hbr-card",
  "hbr-input",
  "hbr-icon",
  "hbr-table",
  "hbr-button",
  "hbr-spinner",
  "hbr-pagination",
]);

const tableId = "predictive_devicepair_table_main";
// default sort column and sort order
let defaultSort = [["remoteDevice", "asc"]];

const getExportCellValue = (item, key) => {
  const exportValueMap = {
    location:
      typeof item?.location === "string"
        ? item.location.replace(/<[^>]*>/g, "")
        : item?.location,
  };

  return exportValueMap[key] ?? item?.[key] ?? "";
};

const SitesTable = (props) => {
  const { tViews } = useI18n("views");
  const navigate = useNavigate();
  const dataRef = useRef({ timestamp: 0, error: null });
  const [loader] = useState(false);
  const [exportLoader, setExportLoader] = useState(false);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const openTableSettings = () => {
    tableStore.setState({ settingsOpen: true });
  };
  const { endpoints } = props.globalFilter;
  const { selectedOverlay, onboardingStatus, appGroup } = validateProps(
    props.globalFilter
  );
  const { state, getAppGroupSingleSiteTableData } = useApplicationInfoHooks();
  const [tablePathData, setTablePathData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const columnsHook = useColumns({
    config: ({ store }) => {
      return getTableColumns(tableId, store, openTableSettings, navigate);
    },
    order: defaultSort,
  });

  const tableStore = useMemo(() => {
    const store = new TableStore({
      data: tablePathData,
      query: { page: 0, pageLength: 30 },
      hooks: [columnsHook],
      fetchData: async ({ setSource, setCount, query }) => {
        const timestamp = Date.now();
        Object.assign(dataRef.current, { timestamp, error: null });
        try {
          const searchTerm = query.search || "";
          let tableDataResult = [];
          let totalCount = 0;
          if (tablePathData && Array.isArray(tablePathData)) {
            // Filter data based on search query
            if (searchTerm.trim()) {
              tableDataResult = tablePathData.filter((item) => {
                // Search across multiple fields
                return Object.values(item).some(
                  (value) =>
                    value &&
                    value
                      .toString()
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                );
              });
            } else {
              tableDataResult = tablePathData;
            }
            // Only sort if query.order is present and valid;
            if (
              query.order &&
              Array.isArray(query.order) &&
              query.order.length > 0
            ) {
              const [sortCol, sortOrder] = query.order[0];
              tableDataResult = [...tableDataResult].sort((a, b) => {
                if (a[sortCol] == null) return 1;
                if (b[sortCol] == null) return -1;
                if (a[sortCol] < b[sortCol])
                  return sortOrder === "asc" ? -1 : 1;
                if (a[sortCol] > b[sortCol])
                  return sortOrder === "asc" ? 1 : -1;
                return 0;
              });
            }
            totalCount = tableDataResult.length;
            setCount(totalCount, false); // Update count BEFORE slicing for pagination
            // Pagination logic: slice the data for the current page
            const { page = 0, pageLength = 30 } = query;
            const startIdx = page * pageLength;
            const endIdx = startIdx + pageLength;
            tableDataResult = tableDataResult.slice(startIdx, endIdx);
          }
          setSource(tableDataResult);
          setIsLoadingComplete(true);
        } catch (error) {
          console.error("Error filtering table data:", error);
          dataRef.current.error = error;
        }
      },
    });
    useTableSettings(
      LOCAL_STORAGE_LABELS.PATHDEVICEPAIR_TABLE,
      [],
      columnsHook,
      store
    );
    return store;
  }, [tablePathData]);

  const { source, columns, query, count } = tableStore.getState();
  const { search, pageLength, page } = query;
  const onSearch = ({ target: { value } }) => {
    if (value !== search) tableStore.setQuery({ search: value });
  };

  const getExportData = async () => {
    setExportLoader(true);
    if (tablePathData?.length > 0) {
      const { data, query } = tableStore.getState();
      const searchTerm = query.search || "";
      // Filter data based on search query (same logic as fetchData)
      let filteredData = data;
      if (searchTerm.trim()) {
        filteredData = data.filter((item) => {
          return Object.values(item).some(
            (value) =>
              value &&
              value.toString().toLowerCase().includes(searchTerm.toLowerCase())
          );
        });
      }
      const exportColumns = columns.filter(
        (col) => col.name && col.prop !== "actions" && col.prop !== "action"
      );
      const columnTitles = exportColumns.map((item) =>
        escapeCSVField(item.name)
      );
      const csvData = [columnTitles.join(",")];
      const columnKeys = exportColumns.map((item) => item.prop);
      for (const item of filteredData) {
        const keys = columnKeys.map((key) =>
          escapeCSVField(getExportCellValue(item, key))
        );
        csvData.push(keys.join(","));
      }
      downloadAsCSVFile(csvData.join("\n"), tViews("singleSitePathTableLabel"));
    }
    setExportLoader(false);
  };

  // set loading complete state based on loader state
  useEffect(() => {
    setIsLoadingComplete(!loader);
  }, [loader]);

  const [, forceUpdate] = useState(null);

  useEffect(() => {
    const unsubscribe = tableStore.subscribe(forceUpdate);
    return () => unsubscribe();
  }, [tableStore]);

  useEffect(() => {
    const siteId = parseInt(getLocalStorageFlag("actionCardSite"));

    if (onboardingStatus === "ACTIVE") {
      setIsLoading(true);

      if (siteId === -1) {
        setIsLoading(false);
        return;
      }

      let endpoint = null;

      if (endpoints && !_.isEmpty(endpoints[siteId.toString()])) {
        setLocalStorageFlag(
          "endpoints",
          JSON.stringify(endpoints[siteId.toString()])
        );
      }
      if (!endpoints) {
        if (getLocalStorageFlag("endpoints")) {
          endpoint = JSON.parse(getLocalStorageFlag("endpoints"));
        }
      } else {
        endpoint = endpoints[siteId.toString()];
      }

      getAppGroupSingleSiteTableData(endpoint, selectedOverlay);
    } else {
      setIsLoading(false);
    }
  }, [appGroup, endpoints, onboardingStatus, selectedOverlay]);

  useEffect(() => {
    const updatedTable = (data) => {
      const devData = [...data].sort((a, b) => b.gains - a.gains);
      setTablePathData(devData);
    };

    if (
      state.isFetchingAppGroupPerSingleSiteTableDataDone &&
      Array.isArray(state.singleSiteTableData)
    ) {
      updatedTable(state.singleSiteTableData);
      setIsLoading(false);
    } else if (
      state.isFetchingAppGroupPerSingleSiteTableDataError.errorObject
    ) {
      setIsLoading(false);
    }
  }, [
    state.isFetchingAppGroupPerSingleSiteTableDataDone,
    state.singleSiteTableData,
    state.isFetchingAppGroupPerSingleSiteTableDataError,
  ]);

  if (isLoading) {
    return (
      <div className={css["remote-site-table"]} data-cy="pdeTableView">
        <div id="remote-sites-spinner" data-cy="spinner-remoteSites">
          <Spinner />
        </div>
      </div>
    );
  } else if (onboardingStatus !== "ACTIVE") {
    return (
      <div className={css["remote-site-table"]} data-cy="pdeTableView">
        <Suspense fallback={<Spinner />}>
          <NoDataAvailable text={tViews("syncronizedChartNoData")} />
        </Suspense>
      </div>
    );
  } else if (state.isFetchingAppGroupPerSingleSiteTableDataError.errorObject) {
    return (
      <HbrCard
        className={css["remote-site-table"]}
        data-cy="pde-table-view-error"
      >
        <Suspense fallback={<Spinner />}>
          <ErrorComponent
            {...state.isFetchingAppGroupPerSingleSiteTableDataError
              ?.errorObject}
            width={"200px"}
            className={"super-small-dashlet-error"}
          />
        </Suspense>
      </HbrCard>
    );
  } else if (state.isFetchingAppGroupPerSingleSiteTableDataDone) {
    return (
      <HbrCard
        className={css["remote-site-table"]}
        data-cy="pde-table-view-single-site"
      >
        <div>
          <div className="hbr-css__layout-col-md">
            <div
              className="hbr-css__text-heading-lg-bold"
              role="heading"
              aria-level={"2"}
              data-cy="c360-flow-tab"
            >
              {tViews("singleSitePathTableLabel")}
            </div>
          </div>
          <div className="table-container hbr-css__layout-col-md">
            {loader === true && <OverLappingSpinner />}
            <div aria-live="assertive" className="loading_completed">
              {isLoadingComplete}
            </div>
            <div className="hbr-css__layout-col-md">
              <div className="hbr-css__layout-row-xs hbr-css__layout-justify-between">
                <div className="hbr-css__layout-row-xs">
                  <HbrInput
                    placeholder={tViews("a11yLabels.megaMenuSearchSite")}
                    clearable
                    data-testid="pw-recommended-table-search-input"
                    disabled={loader}
                    value={search}
                    onHbr-clear={onSearch}
                    onkeydown={(event) => {
                      if (event.code === "Enter") onSearch(event);
                    }}
                    onHbr-blur={onSearch}
                  >
                    <HbrIcon
                      slot="prefix"
                      name="magnifying-glass"
                      sentiment="neutral"
                    />
                  </HbrInput>
                  <div className="hbr-css__filter-description">
                    {tViews("tabViewSiteswithCount", { count: count })}
                  </div>
                </div>
                {count > 0 ? (
                  <HbrButton
                    variant="outline"
                    onClick={getExportData}
                    disabled={loader === true || exportLoader === true}
                  >
                    {exportLoader === true ? (
                      <HbrSpinner slot="prefix" />
                    ) : (
                      <HbrIcon slot="prefix" name="upload-simple" />
                    )}
                    {tViews("export")}
                  </HbrButton>
                ) : null}
              </div>
            </div>
            <HbrTable
              id={tableId}
              data-testid={tableId}
              columns={columns}
              readonly
              resize
              className="predictive-site-table"
              frameSize={source.length}
              serverSide
              headerSlot={true}
              externalSorting
              canFocus={false}
              useClipboard={false}
              onHbr-before-sort={(event) => {
                const { column, order } = event.detail;
                const columnsHook = tableStore.getHook("columns");
                if (order) columnsHook.sort({ order: [[column.id, order]] });
                else if (column.id === defaultSort[0][0])
                  columnsHook.sort({ order: [[column.id, "asc"]] });
                else columnsHook.sort({ order: defaultSort });
              }}
              source={source}
            >
              <NoData
                count={count}
                loader={loader}
                error={dataRef.current.error}
                text={tViews("c360.tEmpty")}
              />
            </HbrTable>
            <HbrPagination
              orderArray={[1, 2, 0]}
              showAll
              disabled={loader}
              tableStore={tableStore}
              pageSize={pageLength}
              itemCount={count}
              page={page}
              size="small"
              onHbr-page-changed={(event) => {
                const index = Number.isInteger(event.detail) ? event.detail : 0;
                tableStore.setQuery({ page: index });
              }}
              onHbr-size-changed={(event) => {
                tableStore.setQuery({
                  pageLength: event.detail.pageSize,
                  page: 0,
                });
              }}
            />
          </div>
          <SettingsDrawer
            tableStore={tableStore}
            localStorageName={LOCAL_STORAGE_LABELS.PATHDEVICEPAIR_TABLE}
            defaultToggles={[]}
            dataTestid="predictive_nw_device_pair_table"
          />
        </div>
      </HbrCard>
    );
  } else {
    return (
      <div
        className={css["remote-site-table"]}
        data-cy="pde-table-view-spinner"
      >
        <Suspense fallback={<Spinner />}>
          <NoDataAvailable text={tViews("syncronizedChartNoData")} />
        </Suspense>
      </div>
    );
  }
};

SitesTable.propTypes = {
  globalFilter: PropTypes.object,
};

export default connect(mapStateToProps)(AppHOC(SitesTable));
